// modules/reporteFacturas/pages/ReporteFacturasTotalPage.jsx
// Migracion de reporte_facturas_total.php (PHP legado) — reporte de todas
// las facturas emitidas (no pre-facturas) filtrado por sucursal + mes/año,
// con el desglose por categoria (Serv Tecnico, Rastreo, Descuentos, Ventas,
// Alquiler Radio, Comunicacion) que ya usaba el reporte viejo.
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Button, TextField, MenuItem, Typography, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  TableSortLabel,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import { getReporteFacturasTotal, exportarReporteFacturasTotal } from "../services/reporteFacturasService";
import { imprimirFactura } from "../../facturacion/services/facturacionService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiSelect-select": { py: "6px" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const MESES = [
  { value: "Todo", label: "Todo (año completo)" },
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];

const celdaTruncada = { maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz", "Todo"];

const PRE_FAC_VALIDOS = ["no", "si", "todos"];
const OPCIONES_PRE_FAC = [
  { value: "no", label: "Solo facturas" },
  { value: "si", label: "Solo pre-facturas" },
  { value: "todos", label: "Facturas y pre-facturas" },
];

const CATEGORIAS = [
  { key: 1, label: "Serv Tecnico + Alquiler" },
  { key: 2, label: "Rastreo Internacional" },
  { key: 3, label: "Rastreo Nacional" },
  { key: 4, label: "Descuentos" },
  { key: 5, label: "Ventas" },
  { key: 6, label: "Alquiler Radio" },
  { key: 7, label: "Servicio de comunicacion" },
];

const MONEDAS = { 1: "Dólares", 2: "Bolivianos" };

// Id, Grupo, Cliente, Nombre, Servicio, Descripcion, Grupo llave, Nombre Factura, N Factura, Sucursal, CUF
// + las categorias + Cantidad, Fecha Emision, Mes Servicio, Monto Bs, Monto $us, Moneda, Imprimir
const TOTAL_COLUMNAS = 11 + CATEGORIAS.length + 7;

const normalizarSucursal = (value) => {
  const s = String(value || "").trim().toLowerCase();
  if (s.includes("santa cruz")) return "Santa Cruz";
  if (s.includes("cochabamba")) return "Cochabamba";
  if (s.includes("la paz") || s.includes("el alto")) return "La Paz";
  return "Todo";
};

const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 12 }, (_, i) => anioActual + 1 - i); // anio+1 .. anio-10

// Coma para miles, punto para decimales — mismo formato que number_format()
// de PHP (usado tal cual en todos los reportes legados que se migraron).
const formatMoneda = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// El backend manda "fecha" como string plano "YYYY-MM-DD" (sin hora, sin
// zona horaria — ver reporteFacturasModel FECHA_REPORTE). Se parsea a mano
// en vez de "new Date(value)": un string sin zona se interpreta como
// medianoche UTC, y extraerlo con getters locales en un huso negativo
// (Bolivia, UTC-4) mostraria un dia menos.
const formatFecha = (value) => {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "-";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

// Valor a comparar por columna — las categorias (cat1..cat7) leen de
// r.detalle, "fecha" se compara como timestamp, el resto es directo.
const getValorOrdenable = (row, col) => {
  if (col.startsWith("cat")) return Number(row.detalle?.[Number(col.slice(3))]) || 0;
  if (col === "fecha") return row.fecha ? new Date(row.fecha).getTime() : 0;
  return row[col];
};

const compararValores = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "string" || typeof b === "string") {
    return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
  }
  return a - b;
};

// Cabecera clickeable — misma columna de nuevo invierte el sentido, otra
// columna arranca en ascendente (comportamiento estandar de MUI).
const SortableHeaderCell = ({ col, label, align, orderBy, orderDir, onSort }) => (
  <StyledHeaderCell align={align} sortDirection={orderBy === col ? orderDir : false}>
    <TableSortLabel
      active={orderBy === col}
      direction={orderBy === col ? orderDir : "asc"}
      onClick={() => onSort(col)}
    >
      {label}
    </TableSortLabel>
  </StyledHeaderCell>
);

const ReporteFacturasTotalPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // Si viene del indice (/reporte-facturas), los filtros ya llegan armados
  // por query string — se precargan y se dispara la busqueda sola.
  const [sucursal, setSucursal] = useState(() => searchParams.get("sucursal") || normalizarSucursal(user?.sucursal));
  const [mes, setMes] = useState(() => {
    const m = searchParams.get("mes");
    if (!m) return new Date().getMonth() + 1;
    return m === "Todo" ? "Todo" : Number(m);
  });
  const [year, setYear] = useState(() => Number(searchParams.get("year")) || anioActual);
  const [preFac, setPreFac] = useState(() => {
    const p = searchParams.get("preFac");
    return PRE_FAC_VALIDOS.includes(p) ? p : "no";
  });
  const [items, setItems] = useState([]);
  const [totales, setTotales] = useState({ bolivianos: 0, dolares: 0 });
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportando, setExportando] = useState(false);
  const [abriendoCod, setAbriendoCod] = useState(null);
  const [orderBy, setOrderBy] = useState(null);
  const [orderDir, setOrderDir] = useState("asc");

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(col);
      setOrderDir("asc");
    }
  };

  const itemsOrdenados = useMemo(() => {
    if (!orderBy) return items;
    const copia = [...items].sort((a, b) =>
      compararValores(getValorOrdenable(a, orderBy), getValorOrdenable(b, orderBy))
    );
    if (orderDir === "desc") copia.reverse();
    return copia;
  }, [items, orderBy, orderDir]);

  const filtrosActuales = useMemo(
    () => ({ sucursal, year, mes, preFac }),
    [sucursal, year, mes, preFac]
  );

  const handleBuscar = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getReporteFacturasTotal(filtrosActuales);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotales(data?.totales || { bolivianos: 0, dolares: 0 });
      setBuscado(true);
    } catch (e) {
      setItems([]);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get("sucursal")) {
      handleBuscar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportar = async () => {
    setExportando(true);
    setError("");
    try {
      const response = await exportarReporteFacturasTotal(filtrosActuales);
      const url = URL.createObjectURL(new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "reporte_facturas_total.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo exportar el reporte.");
    } finally {
      setExportando(false);
    }
  };

  const handleAbrirFactura = async (cod) => {
    setAbriendoCod(cod);
    try {
      const response = await imprimirFactura(cod);
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      setError("No se pudo abrir la factura.");
    } finally {
      setAbriendoCod(null);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title={
          preFac === "todos" ? "Reporte de Facturas y Pre-Facturas"
            : preFac === "si" ? "Reporte de Pre-Facturas"
            : "Reporte de Facturas"
        }
        subtitle="Total facturado por sucursal, mes y año"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
          <TextField
            select size="small" label="Sucursal"
            sx={{ ...compactFieldSx, width: 150 }}
            value={sucursal}
            onChange={(e) => setSucursal(e.target.value)}
          >
            {SUCURSALES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>
            ))}
          </TextField>

          <TextField
            select size="small" label="Mes"
            sx={{ ...compactFieldSx, width: 170 }}
            value={mes}
            onChange={(e) => setMes(e.target.value)}
          >
            {MESES.map((m) => (
              <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.85rem" }}>{m.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select size="small" label="Año"
            sx={{ ...compactFieldSx, width: 110 }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {ANIOS.map((a) => (
              <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>
            ))}
          </TextField>

          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            onClick={handleBuscar}
            disabled={loading}
            sx={{ minHeight: 32 }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>

          <TextField
            select size="small" label="Tipo"
            sx={{ ...compactFieldSx, width: 190 }}
            value={preFac}
            onChange={(e) => setPreFac(e.target.value)}
          >
            {OPCIONES_PRE_FAC.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.85rem" }}>{o.label}</MenuItem>
            ))}
          </TextField>

          {buscado && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Total: {items.length}
            </Typography>
          )}

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={exportando ? <CircularProgress size={14} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExportar}
            disabled={exportando || !buscado || items.length === 0}
            sx={{ minHeight: 32 }}
          >
            {exportando ? "Exportando..." : "Exportar Excel"}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!buscado ? (
          <Alert severity="info">Elige sucursal, mes y año, y presiona Buscar.</Alert>
        ) : !loading && items.length === 0 ? (
          <Alert severity="info">No hay facturas para los filtros seleccionados.</Alert>
        ) : (
          <>
            <Paper variant="outlined">
              <TableContainer sx={{ maxHeight: "65vh" }}>
                <Table size="small" stickyHeader sx={{ "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } }}>
                  <TableHead>
                    <TableRow>
                      {[
                        { col: "cod", label: "Id" },
                        { col: "nombre", label: "Cliente" },
                        { col: "grupo", label: "Grupo" },
                        { col: "nombre_servicio", label: "Nombre" },
                        { col: "descrip", label: "Descripción" },
                        { col: "serv", label: "Servicio" },
                        { col: "cod_llave_nombre", label: "Grupo llave" },
                        { col: "fac_nombre", label: "Nombre Factura" },
                        { col: "fac", label: "N° Factura" },
                        { col: "sucursal_facturar", label: "Sucursal" },
                        { col: "cuf", label: "CUF" },
                      ].map((c) => (
                        <SortableHeaderCell key={c.col} {...c} orderBy={orderBy} orderDir={orderDir} onSort={handleSort} />
                      ))}
                      {CATEGORIAS.map((c) => (
                        <SortableHeaderCell
                          key={c.key} col={`cat${c.key}`} label={c.label} align="right"
                          orderBy={orderBy} orderDir={orderDir} onSort={handleSort}
                        />
                      ))}
                      {[
                        { col: "cantidad_detalle", label: "Cantidad", align: "right" },
                        { col: "fecha", label: "Fecha de Emisión" },
                        { col: "mes_servicio", label: "Mes de Servicio" },
                        { col: "bolivianos", label: "Monto Bs", align: "right" },
                        { col: "dolares", label: "Monto $us", align: "right" },
                        { col: "moneda", label: "Moneda" },
                      ].map((c) => (
                        <SortableHeaderCell key={c.col} {...c} orderBy={orderBy} orderDir={orderDir} onSort={handleSort} />
                      ))}
                      <StyledHeaderCell>Imprimir</StyledHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={TOTAL_COLUMNAS} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      itemsOrdenados.map((r) => (
                        <TableRow
                          key={r.cod}
                          hover
                          sx={r.estado === "X" ? { bgcolor: (t) => alpha(t.palette.error.main, 0.08) } : undefined}
                        >
                          <TableCell>{r.cod}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Button
                              size="small"
                              variant="text"
                              disabled={abriendoCod === r.cod}
                              onClick={() => handleAbrirFactura(r.cod)}
                              title={r.nombre}
                              sx={{
                                fontSize: "0.74rem", textTransform: "none", p: 0, minWidth: 0, minHeight: 0,
                                justifyContent: "flex-start", display: "block", maxWidth: "100%",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              }}
                            >
                              {abriendoCod === r.cod ? "Abriendo..." : r.nombre}
                            </Button>
                          </TableCell>
                          <TableCell>{r.grupo || "-"}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.nombre_servicio}>{r.nombre_servicio || "-"}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.descrip}>{r.descrip}</TableCell>
                          <TableCell>{r.serv}</TableCell>
                          <TableCell>{r.cod_llave_nombre || "-"}</TableCell>
                          <TableCell sx={celdaTruncada} title={r.fac_nombre}>{r.fac_nombre || "-"}</TableCell>
                          <TableCell>{r.fac}</TableCell>
                          <TableCell>{r.sucursal_facturar}</TableCell>
                          <TableCell sx={{ ...celdaTruncada, maxWidth: 130 }} title={r.cuf}>{r.cuf || "-"}</TableCell>
                          {CATEGORIAS.map((c) => (
                            <TableCell key={c.key} align="right">{formatMoneda(r.detalle?.[c.key])}</TableCell>
                          ))}
                          <TableCell align="right">{r.cantidad_detalle}</TableCell>
                          <TableCell>{formatFecha(r.fecha)}</TableCell>
                          <TableCell>{r.mes_servicio}</TableCell>
                          <TableCell align="right">{formatMoneda(r.bolivianos)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.dolares)}</TableCell>
                          <TableCell>{MONEDAS[r.moneda] || "-"}</TableCell>
                          <TableCell>
                            {r.qr ? (
                              <a href={r.qr} target="_blank" rel="noopener noreferrer">Ver en SIAT</a>
                            ) : (
                              <Button
                                size="small"
                                variant="text"
                                disabled={abriendoCod === r.cod}
                                onClick={() => handleAbrirFactura(r.cod)}
                                sx={{ fontSize: "0.74rem", textTransform: "none", p: 0, minWidth: 0, minHeight: 0 }}
                              >
                                {abriendoCod === r.cod ? "Abriendo..." : "Imprimir"}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, maxWidth: 320, ml: "auto" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Total bolivianos</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoneda(totales.bolivianos)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                <Typography variant="body2">Total dólares</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoneda(totales.dolares)}</Typography>
              </Box>
            </Paper>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteFacturasTotalPage;
